// components/ApplicationForm.jsx
import React, { useEffect, useState } from "react";
import CustomQuestions from "./CustomQuestions";
import CommonButton from "./CommonButton";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const ApplicationForm = ({
  formData,
  selectedJob,
  resumeFile,
  submitting,
  onInputChange,
  onFileChange,
  onCustomQuestionChange,
  onSubmit,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState(formData.skills || []);

  // 1) If parent updates formData.skills, sync it into local chips
  useEffect(() => {
    if (!arraysEqual(formData.skills || [], chips)) {
      setChips(formData.skills || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.skills]); // intentionally depends only on incoming skills

  // 2) When local chips change, notify parent via onInputChange
  useEffect(() => {
    if (!arraysEqual(formData.skills || [], chips)) {
      onInputChange({
        target: { name: "skills", value: chips },
      });
    }
  }, [chips, formData.skills, onInputChange]);

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Add chip when comma or Enter is typed
    if (value.endsWith(",")) {
      const newChip = value.slice(0, -1).trim();
      if (newChip && !chips.includes(newChip)) {
        setChips((prev) => [...prev, newChip]);
      }
      setInputValue("");
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newChip = inputValue.trim();
      if (newChip && !chips.includes(newChip)) {
        setChips((prev) => [...prev, newChip]);
      }
      setInputValue("");
    }
  };

  const handleDeleteChip = (chipToDelete) => {
    setChips((prev) => prev.filter((chip) => chip !== chipToDelete));
  };

  return (
    <form onSubmit={onSubmit} className="application-form">
      <div className="form-row">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={onInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={onInputChange}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={onInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Phone *</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onInputChange}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={onInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Country *</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={onInputChange}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label>Total Experience (Years) *</label>
        <input
          type="number"
          name="totalExperienceYears"
          value={formData.totalExperienceYears}
          onChange={onInputChange}
          required
          min="0"
        />
      </div>

      <div className="form-group">
        <label>Skills (comma-separated) *</label>
        <div className="chip-input-container">
          <div className="chip-list">
            {chips.map((chip, index) => (
              <div key={index} className="chip">
                {chip}
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => handleDeleteChip(chip)}
                >
                  &times;
                </button>
              </div>
            ))}
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Node.js, MongoDB, AWS"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Current Company *</label>
          <input
            type="text"
            name="currentCompany"
            value={formData.currentCompany}
            onChange={onInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Current Title *</label>
          <input
            type="text"
            name="currentTitle"
            value={formData.currentTitle}
            onChange={onInputChange}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Current CTC (INR) Per Month *</label>
          <input
            type="number"
            name="currentCTC"
            value={formData.currentCTC}
            onChange={onInputChange}
            required
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Expected CTC (INR) Per Month *</label>
          <input
            type="number"
            name="expectedCTC"
            value={formData.expectedCTC}
            onChange={onInputChange}
            required
            min="0"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Earliest Start Date *</label>
        <input
          type="date"
          name="earliestStartDate"
          value={formData.earliestStartDate}
          onChange={onInputChange}
          min={new Date().toISOString().split("T")[0]}
          required
        />
      </div>

      {/* Custom Questions */}
      {selectedJob?.customQuestions?.length > 0 && (
        <CustomQuestions
          questions={selectedJob.customQuestions}
          answers={formData.customAnswers}
          onChange={onCustomQuestionChange}
        />
      )}

      <div className="form-group">
        <label>Upload Resume (PDF) *</label>
        <input type="file" accept=".pdf" onChange={onFileChange} required />
        {resumeFile && <p className="file-name">Selected: {resumeFile.name}</p>}
      </div>

      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            name="privacyConsent"
            checked={formData.privacyConsent}
            onChange={onInputChange}
            required
          />
          <span>I agree to the privacy policy and terms of service *</span>
        </label>
      </div>

      <div className="form-actions">
        <CommonButton
          label="Cancel"
          onClick={onCancel}
          variant="outline"
          className="black-border"
          disabled={submitting}
          type="button"
        />
        <CommonButton
          label={submitting ? "Submitting..." : "Submit Application"}
          className="btn-primary"
          disabled={submitting}
          type="submit"
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              style={{ height: "20px" }}
            >
              <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
            </svg>
          }
        />
      </div>
    </form>
  );
};

export default ApplicationForm;